import clsx from "clsx";
import * as React from "react";
import { NavLink, type NavLinkProps } from "../navlink/Navlink.js";
import { Tooltip, type TooltipProps } from "../tooltip/Tooltip.js";

type SidebarNavLinkProps = Omit<NavLinkProps, "collapsible">;
interface SidebarLinkProps extends SidebarNavLinkProps {
  label: string;
  href: string;
}

interface SidebarSection {
  title: string;
  leftSection?: React.ReactNode;
  links: NavLinkProps[];
  active?: boolean;
}

type SidebarItem = SidebarSection | SidebarLinkProps;

interface CollapsibleSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: number;
  items: SidebarItem[];
  collapsed?: boolean;
}

const isSidebarSection = (item: SidebarItem): item is SidebarSection => {
  return (item as SidebarSection).links !== undefined;
};

const CollapsibleSidebar = React.forwardRef<
  HTMLDivElement,
  CollapsibleSidebarProps
>((props, ref) => {
  const { items, className, collapsed, width = 240, style, ...rest } = props;

  const internalStyles: React.CSSProperties = {
    width: collapsed ? 76 : width,
  };

  const tooltipProps: Omit<TooltipProps, "content" | "children"> = {
    positioning: {
      placement: "right",
    },
    withArrow: true,
    lazyMount: true,
    unmountOnExit: true,
    disabled: !collapsed,
    openDelay: 50,
  };
  return (
    <div
      className={clsx("ox-reset ox-CollapsibleSidebar", className)}
      ref={ref}
      style={{
        ...internalStyles,
        ...style,
      }}
      data-collapsed={collapsed}
      {...rest}
    >
      {items?.map((item, index) => {
        if (isSidebarSection(item)) {
          return (
            <Tooltip key={index} content={item.title} {...tooltipProps}>
              <NavLink
                component="span"
                label={item.title}
                active={item.active}
                withNestedPadding={false}
                leftSection={
                  item.leftSection || (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"></path>
                    </svg>
                  )
                }
              >
                {item?.links?.map((link, index) => {
                  return (
                    <Tooltip key={index} content={link.label} {...tooltipProps}>
                      <NavLink
                        href={link.href}
                        label={link.label}
                        withNestedPadding={false}
                        active={item.active}
                        leftSection={
                          link.leftSection || (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              viewBox="0 0 256 256"
                            >
                              <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"></path>
                            </svg>
                          )
                        }
                      />
                    </Tooltip>
                  );
                })}
              </NavLink>
            </Tooltip>
          );
        } else {
          return (
            <Tooltip
              disabled={!collapsed}
              content={item.label}
              key={index}
              withArrow
              positioning={{
                placement: "right",
              }}
              {...tooltipProps}
            >
              <NavLink
                href={item.href}
                label={item.label}
                withNestedPadding={false}
                active={item.active}
                leftSection={
                  item.leftSection || (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"></path>
                    </svg>
                  )
                }
              />
            </Tooltip>
          );
        }
      })}
    </div>
  );
});

CollapsibleSidebar.displayName = "CollapsibleSidebar";
export { CollapsibleSidebar };
